import { CSSProperties, MouseEvent, ReactNode, useState } from 'react';

import { Box, Popper, Paper, Fade, styled } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import { Dimens } from '~themes';

import TooltipStyle from './enums/tooltipStyle';

// The custom tooltip component
interface TooltipProps {
	children: ReactNode;
	content: ReactNode;
	placement?: 'top' | 'bottom' | 'left' | 'right';
	style?: TooltipStyle;
	arrow?: boolean;
	containerSx?: SxProps<Theme>;
}

interface TooltipToken {
	foregroundColor: string;
	backgroundColor: string;
	font: CSSProperties;
	padding: string;
	borderRadius: string;
}

const getTooltipToken = (theme: Theme, tooltipStyle: TooltipStyle): TooltipToken => {
	switch (tooltipStyle) {
		case TooltipStyle.Extended:
			return {
				foregroundColor: theme.palette.textPrimary.main,
				backgroundColor: theme.palette.bgPrimary.main,
				font: theme.typography.labelSm,
				padding: Dimens.spacingMd,
				borderRadius: Dimens.radiusMd,
			};
		case TooltipStyle.Compact:
		default:
			return {
				foregroundColor: theme.palette.textWhite.main,
				backgroundColor: theme.palette.bgOverlay.main,
				font: theme.typography.labelSm,
				padding: Dimens.spacingMd,
				borderRadius: Dimens.radiusMd,
			};
	}
};

const TooltipContainer = styled(Paper, {
	name: 'Tooltip',
	slot: 'Container',
	shouldForwardProp: (prop) => prop !== 'tooltipStyle',
})<{ tooltipStyle: TooltipStyle }>(({ theme, tooltipStyle }) => {
	const token = getTooltipToken(theme, tooltipStyle);

	return {
		'--tooltip-arrow-color': token.backgroundColor,
		padding: token.padding,
		backgroundColor: token.backgroundColor,
		color: token.foregroundColor,
		borderRadius: token.borderRadius,
		fontFamily: token.font.fontFamily,
		fontWeight: token.font.fontWeight,
		fontSize: token.font.fontSize,
		lineHeight: token.font.lineHeight,
		letterSpacing: token.font.letterSpacing,
		position: 'relative',
		boxShadow: 'none',
		filter: 'drop-shadow(0px 1px 4px rgba(0, 0, 0, 0.16))',
		boxSizing: 'border-box',
		width: 360,
		maxWidth: `calc(100vw - ${Dimens.spacing4xl})`,
	};
});

function Tooltip({
	children,
	content,
	placement = 'bottom',
	style = TooltipStyle.Compact,
	arrow = false,
	containerSx,
}: TooltipProps) {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [arrowEl, setArrowEl] = useState<null | HTMLElement>(null);
	const [open, setOpen] = useState(false);

	// Show tooltip on mouse enter
	const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
		setOpen(true);
	};

	// Hide tooltip on mouse leave
	const handleMouseLeave = () => {
		setOpen(false);
		setAnchorEl(null);
	};

	return (
		<Box
			component='span'
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			sx={{ display: 'flex' }}
		>
			{children}

			<Popper
				open={open}
				anchorEl={anchorEl}
				placement={placement}
				transition
				sx={(theme) => ({
					zIndex: theme.zIndex.tooltip,
					'&[data-popper-placement*="top"] .Tooltip-arrow': { bottom: -5 },
					'&[data-popper-placement*="bottom"] .Tooltip-arrow': { top: -5 },
					'&[data-popper-placement*="left"] .Tooltip-arrow': { right: -5 },
					'&[data-popper-placement*="right"] .Tooltip-arrow': { left: -5 },
				})}
				modifiers={[
					{
						name: 'offset',
						options: {
							offset: [0, arrow ? 7 : 2],
						},
					},
					{
						name: 'arrow',
						enabled: arrow,
						options: {
							element: arrowEl,
							padding: 8,
						},
					},
				]}
			>
				{({ TransitionProps }) => (
					<Fade {...TransitionProps} timeout={200}>
						<TooltipContainer elevation={0} tooltipStyle={style} sx={containerSx}>
							{arrow && (
								<Box
									ref={setArrowEl}
									data-popper-arrow
									className='Tooltip-arrow'
									sx={{
										position: 'absolute',
										width: 10,
										height: 10,
										'&::before': {
											content: '""',
											display: 'block',
											width: 10,
											height: 10,
											backgroundColor: 'var(--tooltip-arrow-color)',
											transform: 'rotate(45deg)',
										},
									}}
								/>
							)}
							{content}
						</TooltipContainer>
					</Fade>
				)}
			</Popper>
		</Box>
	);
}

export default Tooltip;
